import React, { useState } from 'react';
import { Box, Button, Chip, Collapse, Paper, TextField, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { ApprovalFieldDef, ApprovalTermFieldStatus } from 'modules/creditSubmission/submissionDetails/types';

export const APPROVAL_FIELDS: ApprovalFieldDef[] = [
  // Core Terms
  { key: 'lenderName', label: 'Lender Name', type: 'text', section: 'Core Terms' },
  { key: 'decision', label: 'Decision', type: 'text', section: 'Core Terms' },
  { key: 'applicantName', label: 'Applicant Name', type: 'text', section: 'Core Terms' },
  { key: 'approvalAmount', label: 'Approved Amount', type: 'currency', section: 'Core Terms' },
  { key: 'termLengthMonths', label: 'Term (Months)', type: 'number', section: 'Core Terms' },
  { key: 'monthlyPayment', label: 'Monthly Payment (Customer Rate)', type: 'currency', section: 'Core Terms' },
  { key: 'dealerBuyRate', label: 'Buy Rate (Parsed)', type: 'percentage', section: 'Core Terms', readOnly: true },
  { key: 'customerRate', label: 'Customer Rate', type: 'percentage', section: 'Core Terms' },
  { key: 'downPaymentToVendor', label: 'Down Payment', type: 'currency', section: 'Core Terms' },
  { key: 'docFees', label: 'Doc Fees', type: 'currency', section: 'Core Terms' },
  {
    key: 'minimumRequiredDownPayment',
    label: 'Minimum Required Down Payment',
    type: 'currency',
    section: 'Core Terms',
  },
  { key: 'daysToFirstPayment', label: 'Days To First Payment', type: 'number', section: 'Core Terms' },
  { key: 'typeOfFinancing', label: 'Type Of Financing', type: 'text', section: 'Core Terms' },
  // Program & Terms
  { key: 'programName', label: 'Program Name', type: 'text', section: 'Program & Terms' },
  { key: 'residualAmount', label: 'Residual Amount', type: 'currency', section: 'Program & Terms' },
  { key: 'balloonPayment', label: 'Balloon Payment', type: 'currency', section: 'Program & Terms' },
  { key: 'irregularPayments', label: 'Irregular Payments', type: 'text', section: 'Program & Terms' },
  // Rates & Structure
  { key: 'apr', label: 'APR', type: 'percentage', section: 'Rates & Structure' },
  { key: 'totalDueAtSigning', label: 'Total Due At Signing', type: 'currency', section: 'Rates & Structure' },
  { key: 'advancePaymentsCount', label: 'Advance Payments Count', type: 'number', section: 'Rates & Structure' },
  { key: 'paymentCalculationType', label: 'Payment Calculation Type', type: 'text', section: 'Rates & Structure' },
  // Deal Details
  { key: 'invoiceAmount', label: 'Invoice Amount', type: 'currency', section: 'Deal Details' },
  { key: 'salesPrice', label: 'Sales Price', type: 'currency', section: 'Deal Details' },
  { key: 'cashDown', label: 'Cash Down', type: 'currency', section: 'Deal Details' },
  { key: 'netTradeIn', label: 'Net Trade-In', type: 'currency', section: 'Deal Details' },
  { key: 'taxesAndFees', label: 'Taxes & Fees', type: 'currency', section: 'Deal Details' },
  { key: 'dealerCommission', label: 'Dealer Commission', type: 'currency', section: 'Deal Details' },
  // Parties
  { key: 'underwriterName', label: 'Underwriter Name', type: 'text', section: 'Parties' },
  { key: 'coBorrowerName', label: 'Co-Borrower Name', type: 'text', section: 'Parties' },
  { key: 'dealerName', label: 'Dealer Name', type: 'text', section: 'Parties' },
  { key: 'guarantorName', label: 'Guarantor Name', type: 'text', section: 'Parties' },
  // Dates
  { key: 'decisionDate', label: 'Decision Date', type: 'date', section: 'Dates' },
  { key: 'firstPaymentDueDate', label: 'First Payment Due Date', type: 'date', section: 'Dates' },
  // Details (collapsed — not auto-expanded)
  { key: 'expirationDate', label: 'Buy Rate Expiry Date', type: 'date', section: 'Details' },
  { key: 'recourseType', label: 'Recourse Type', type: 'text', section: 'Details' },
];

const VISIBLE_SECTION_ORDER = [
  'Core Terms',
  'Program & Terms',
  'Rates & Structure',
  'Deal Details',
  'Parties',
  'Dates',
] as const;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatusIcon: React.FC<{ status: ApprovalTermFieldStatus }> = ({ status }) => {
  if (status === 'extracted') return <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />;
  if (status === 'edited') return <EditIcon sx={{ fontSize: 18, color: 'primary.main' }} />;
  return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />;
};

interface EditableFieldRowProps {
  label: string;
  value: string;
  displayValue: string;
  status: ApprovalTermFieldStatus;
  onChange: (val: string) => void;
  onSave: () => void;
  type?: 'text' | 'number' | 'date';
  isDecisionField?: boolean;
  readOnly?: boolean;
}

interface ApprovalDetailsPanelProps {
  form: Record<string, string>;
  originalValues: Record<string, string>;
  getFieldStatus: (key: string) => ApprovalTermFieldStatus;
  getDisplayValue: (key: string, fDef: ApprovalFieldDef) => string;
  onFieldChange: (key: string) => (val: string) => void;
  onSave: () => void;
}

const EditableFieldRow: React.FC<EditableFieldRowProps> = ({
  label,
  value,
  displayValue,
  status,
  onChange,
  onSave,
  type = 'text',
  isDecisionField,
  readOnly = false,
}) => {
  const [editing, setEditing] = useState(false);

  const handleSave = () => {
    setEditing(false);
    onSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      {readOnly ? <LockIcon sx={{ fontSize: 18, color: 'text.disabled' }} /> : <StatusIcon status={status} />}
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, flexShrink: 0, fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>
        {readOnly ? (
          <Typography variant="body2" color={!displayValue ? 'text.disabled' : 'text.primary'} sx={{ py: 0.5, px: 1 }}>
            {displayValue || '—'}
          </Typography>
        ) : isDecisionField && !editing ? (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label={displayValue || 'Unknown'}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        ) : editing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              value={value}
              onChange={e => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              type={type}
              autoFocus
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { borderColor: 'primary.main' },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              sx={{ minWidth: 60, textTransform: 'uppercase', fontWeight: 600 }}
            >
              Save
            </Button>
          </Box>
        ) : (
          <Box
            onClick={() => !readOnly && setEditing(true)}
            sx={{
              cursor: readOnly ? 'default' : 'pointer',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              '&:hover': { bgcolor: readOnly ? 'transparent' : 'action.hover' },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="body2" color={status === 'empty' ? 'text.disabled' : 'text.primary'}>
              {status === 'empty' ? 'Not extracted — click to add' : displayValue}
            </Typography>
            {status === 'edited' && (
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                Edited
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const fieldsBySection = APPROVAL_FIELDS.reduce<Record<string, ApprovalFieldDef[]>>((acc, fDef) => {
  const s = fDef.section ?? 'Core Terms';
  if (!acc[s]) acc[s] = [];
  acc[s].push(fDef);
  return acc;
}, {});

const renderFieldRows = (
  fields: ApprovalFieldDef[],
  form: Record<string, string>,
  getFieldStatus: (key: string) => ApprovalTermFieldStatus,
  getDisplayValue: (key: string, fDef: ApprovalFieldDef) => string,
  onFieldChange: (key: string) => (val: string) => void,
  onSave: () => void
) =>
  fields.map(fDef => {
    const status = getFieldStatus(fDef.key);
    return (
      <EditableFieldRow
        key={fDef.key}
        label={fDef.label}
        value={form[fDef.key] ?? ''}
        displayValue={getDisplayValue(fDef.key, fDef)}
        status={status}
        onChange={onFieldChange(fDef.key)}
        onSave={onSave}
        type={
          fDef.type === 'currency' || fDef.type === 'number' || fDef.type === 'percentage'
            ? 'number'
            : fDef.type === 'date'
              ? 'date'
              : 'text'
        }
        isDecisionField={fDef.key === 'decision'}
        readOnly={fDef.readOnly}
      />
    );
  });

export const ApprovalDetailsPanel: React.FC<ApprovalDetailsPanelProps> = ({
  form,
  originalValues,
  getFieldStatus,
  getDisplayValue,
  onFieldChange,
  onSave,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsFields = fieldsBySection['Details'] ?? [];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Approval Details
        </Typography>
      </Box>

      {VISIBLE_SECTION_ORDER.map(section => {
        const fields = fieldsBySection[section];
        if (!fields?.length) return null;
        return (
          <React.Fragment key={section}>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
              >
                {section}
              </Typography>
            </Box>
            {renderFieldRows(fields, form, getFieldStatus, getDisplayValue, onFieldChange, onSave)}
          </React.Fragment>
        );
      })}

      {/* Collapsed Details section */}
      {detailsFields.length > 0 && (
        <>
          <Box
            onClick={() => setDetailsOpen(v => !v)}
            sx={{
              px: 2,
              py: 0.75,
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, flex: 1 }}
            >
              Details
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                transform: detailsOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s',
              }}
            />
          </Box>
          <Collapse in={detailsOpen}>
            {renderFieldRows(detailsFields, form, getFieldStatus, getDisplayValue, onFieldChange, onSave)}
          </Collapse>
        </>
      )}
    </Paper>
  );
};
